import { useEffect, useState } from "react";
import { ShieldCheck, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const ROLE_COLORS = {
    Commissioner: "#FFB800",
    "Deputy Commissioner": "#FFD34D",
    Referee: "#FF5500",
    Umpire: "#FF7A33",
    Scorekeeper: "#22C55E",
    Statistician: "#A855F7",
    "Technical Official": "#38BDF8",
};

function OfficialCard({ o, index }) {
    const color = ROLE_COLORS[o.role] || "#FF5500";
    const img = o.cutout || o.photo_url;
    return (
        <div
            data-testid={`official-card-${o.id}`}
            className="card-arena relative rounded-3xl overflow-hidden group transition-all duration-300 hover:-translate-y-1.5"
            style={{ boxShadow: "0 0 0 rgba(0,0,0,0)", animationDelay: `${index * 90}ms` }}
        >
            <img
                src="/ph-flag.svg"
                alt=""
                data-testid={`official-flag-${o.id}`}
                className="absolute inset-0 w-full h-full object-cover opacity-[0.14] pointer-events-none"
            />
            <div
                className="relative h-64 flex items-end justify-center overflow-hidden"
                style={{ background: `radial-gradient(ellipse at 50% 105%, ${color}26, transparent 70%)` }}
            >
                <div
                    className="absolute inset-x-8 bottom-0 h-px opacity-60"
                    style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                />
                {img ? (
                    <img
                        src={img}
                        alt={o.name}
                        data-testid={`official-photo-${o.id}`}
                        className={`transition-transform duration-500 group-hover:scale-105 ${
                            o.cutout ? "h-60 object-contain" : "w-full h-64 object-cover object-top"
                        }`}
                        style={o.cutout ? { filter: `drop-shadow(0 0 26px ${color}80)` } : {}}
                    />
                ) : (
                    <ShieldCheck className="w-16 h-16 mb-10 opacity-40" style={{ color }} />
                )}
            </div>
            <div className="p-5 border-t border-[#262638] relative">
                <span
                    className="inline-block text-[10px] font-display font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-full border"
                    style={{ color, borderColor: `${color}55`, background: `${color}14`, textShadow: `0 0 12px ${color}66` }}
                >
                    {o.role}
                </span>
                <h3 data-testid={`official-name-${o.id}`} className="font-display font-black uppercase text-xl text-white mt-2.5 leading-tight">
                    {o.name}
                </h3>
                {o.title && (
                    <p className="font-display italic text-sm mt-1" style={{ color, textShadow: `0 0 14px ${color}55` }}>
                        “{o.title}”
                    </p>
                )}
                {o.bio && <p className="text-sm text-slate-400 mt-2 leading-relaxed">{o.bio}</p>}
            </div>
        </div>
    );
}

export default function Officials() {
    const [officials, setOfficials] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = () =>
            api.get("/officials")
                .then((r) => !cancelled && setOfficials(r.data))
                .catch(() => !cancelled && setOfficials([]));
        load();
        const t = setInterval(load, 12000);
        return () => {
            cancelled = true;
            clearInterval(t);
        };
    }, []);

    return (
        <div data-testid="officials-page" className="relative">
            <section className="relative overflow-hidden">
                <img src="/battle.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#08080C]/60 via-[#08080C]/80 to-[#08080C]" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
                    <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.35em] text-[#FFB800]">
                        The League's Finest Whistles
                    </p>
                    <h1 className="font-display font-black uppercase text-4xl sm:text-6xl leading-none tracking-tight text-white mt-3">
                        Guardians of <span className="bg-gradient-to-b from-white via-[#FFD34D] to-[#FF5500] bg-clip-text text-transparent">the Game</span>
                    </h1>
                    <p className="text-sm sm:text-base text-slate-400 mt-4 max-w-xl">
                        The officials who keep every possession honest. Commanding the court, owning the whistle —
                        the crew every league wishes it had.
                    </p>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 -mt-6 relative">
                {officials === null ? (
                    <div className="flex items-center justify-center py-24 gap-2 text-slate-400">
                        <Loader2 className="w-5 h-5 animate-spin" /> Calling the crew…
                    </div>
                ) : officials.length === 0 ? (
                    <div className="card-arena rounded-3xl p-14 text-center" data-testid="officials-empty">
                        <ShieldCheck className="w-12 h-12 text-[#FF5500] mx-auto mb-4 opacity-60" />
                        <p className="font-display font-bold uppercase text-lg text-white">Officiating crew to be announced</p>
                        <p className="text-sm text-slate-400 mt-2">The league is hand-picking its guardians. Check back soon.</p>
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="officials-grid">
                        {officials.map((o, i) => (
                            <OfficialCard key={o.id} o={o} index={i} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
